<template>
    <Toast />
    <div class="grid platform-settings">
        <div class="col-12">
            <div class="card">
                <div class="settings-header">
                    <span class="settings-header-icon">
                        <i class="pi pi-cog"></i>
                    </span>
                    <div>
                        <h5 class="settings-title">Platform Settings</h5>
                        <p class="settings-subtitle">
                            Platform-wide configuration for ArenaCX site administrators.
                            Changes take effect within a minute, without a deployment.
                        </p>
                    </div>
                </div>

                <div v-if="loading" class="loading-state">
                    <i class="pi pi-spin pi-spinner"></i> Loading settings…
                </div>

                <div v-else class="settings-grid">
                <div class="setting-card">
                    <div class="setting-card-head">
                        <span class="setting-label">Throttle Window</span>
                        <span class="setting-current-badge">Currently {{ savedHuman }}</span>
                    </div>

                    <div class="setting-callout">
                        <i class="pi pi-info-circle"></i>
                        <p>{{ throttleDescription || defaultDescription }}</p>
                    </div>

                    <div class="setting-control">
                        <InputNumber
                            id="throttle-window"
                            v-model="throttleValue"
                            :min="1"
                            :max="unit === 'hours' ? 24 : 1440"
                            :disabled="saving"
                            class="throttle-input"
                            @input="(e) => (throttleValue = e.value)"
                        />
                        <Dropdown
                            v-model="unit"
                            :options="unitOptions"
                            optionLabel="label"
                            optionValue="value"
                            :disabled="saving"
                            class="unit-dropdown"
                            @change="onUnitChange"
                        />
                        <Button
                            label="Save"
                            icon="pi pi-check"
                            class="p-button-primary save-btn"
                            :loading="saving"
                            :disabled="saving || !isDirty"
                            @click="save"
                        />
                    </div>

                    <div class="setting-meta">
                        <span class="meta-equiv">
                            <i class="pi pi-clock"></i> Equivalent to {{ asSeconds.toLocaleString() }} seconds
                        </span>
                        <span v-if="updatedMeta" class="meta-updated">{{ updatedMeta }}</span>
                    </div>
                </div>

                <!-- Soft Bounce Threshold -->
                <div class="setting-card">
                    <div class="setting-card-head">
                        <span class="setting-label">Soft Bounce Threshold</span>
                        <span class="setting-current-badge">Currently {{ savedThreshold }} bounce{{ savedThreshold === 1 ? '' : 's' }}</span>
                    </div>
                    <div class="setting-callout">
                        <i class="pi pi-info-circle"></i>
                        <p>{{ thresholdDescription || defaultThresholdDescription }}</p>
                    </div>
                    <div class="setting-control">
                        <InputNumber
                            id="soft-bounce-threshold"
                            v-model="thresholdValue"
                            :min="2"
                            :max="10"
                            :disabled="savingThreshold"
                            class="throttle-input"
                            @input="(e) => (thresholdValue = e.value)"
                        />
                        <Button
                            label="Save"
                            icon="pi pi-check"
                            class="p-button-primary save-btn"
                            :loading="savingThreshold"
                            :disabled="savingThreshold || !isThresholdDirty"
                            @click="saveThreshold"
                        />
                    </div>
                    <div class="setting-meta">
                        <span class="meta-equiv">
                            <i class="pi pi-shield"></i> Within {{ savedWindowDays }} day{{ savedWindowDays === 1 ? '' : 's' }} of each other
                        </span>
                        <span v-if="thresholdUpdatedMeta" class="meta-updated">{{ thresholdUpdatedMeta }}</span>
                    </div>
                </div>

                <!-- Soft Bounce Window -->
                <div class="setting-card">
                    <div class="setting-card-head">
                        <span class="setting-label">Soft Bounce Window</span>
                        <span class="setting-current-badge">Currently {{ savedWindowDays }} day{{ savedWindowDays === 1 ? '' : 's' }}</span>
                    </div>
                    <div class="setting-callout">
                        <i class="pi pi-info-circle"></i>
                        <p>{{ windowDescription || defaultWindowDescription }}</p>
                    </div>
                    <div class="setting-control">
                        <InputNumber
                            id="soft-bounce-window"
                            v-model="windowValue"
                            :min="7"
                            :max="90"
                            :disabled="savingWindow"
                            class="throttle-input"
                            @input="(e) => (windowValue = e.value)"
                        />
                        <Button
                            label="Save"
                            icon="pi pi-check"
                            class="p-button-primary save-btn"
                            :loading="savingWindow"
                            :disabled="savingWindow || !isWindowDirty"
                            @click="saveWindow"
                        />
                    </div>
                    <div class="setting-meta">
                        <span class="meta-equiv">
                            <i class="pi pi-calendar"></i> Counts bounces over this period
                        </span>
                        <span v-if="windowUpdatedMeta" class="meta-updated">{{ windowUpdatedMeta }}</span>
                    </div>
                </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import InputNumber from 'primevue/inputnumber';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import Toast from 'primevue/toast';
import PlatformSettingsService from '../service/PlatformSettingsService';

const service = new PlatformSettingsService();
const THROTTLE_KEY = 'email_throttle_window_seconds';
const SOFT_BOUNCE_THRESHOLD_KEY = 'soft_bounce_threshold';
const SOFT_BOUNCE_WINDOW_KEY = 'soft_bounce_window_days';

export default {
    name: 'PlatformSettings',
    components: { InputNumber, Dropdown, Button, Toast },
    data() {
        return {
            loading: false,
            saving: false,
            throttleValue: 1,
            unit: 'hours',
            unitOptions: [
                { label: 'Minutes', value: 'minutes' },
                { label: 'Hours', value: 'hours' },
            ],
            // The value (in seconds) currently persisted server-side; used to
            // compute isDirty so Save only enables on an actual change.
            savedSeconds: 3600,
            throttleDescription: '',
            defaultDescription:
                'Controls how long the email-sending service remembers a recent send before allowing the same message to be sent again to the same recipient. During this window, any duplicate send requests for the same recipient and subject are suppressed automatically. Set this value to match your shortest scheduled job interval.',
            updatedBy: null,
            updatedAt: null,

            // Soft Bounce Threshold
            savingThreshold: false,
            thresholdValue: 3,
            savedThreshold: 3,
            thresholdDescription: '',
            defaultThresholdDescription:
                'Number of soft bounces inside the window that triggers auto-escalation of a vendor address from BOUNCED_SOFT to SUPPRESSED. Once SUPPRESSED, the platform stops sending to the address and it appears in the Outdated Vendor Partner Emails report. Lower is more aggressive; higher is more forgiving.',
            thresholdUpdatedBy: null,
            thresholdUpdatedAt: null,

            // Soft Bounce Window
            savingWindow: false,
            windowValue: 30,
            savedWindowDays: 30,
            windowDescription: '',
            defaultWindowDescription:
                'Rolling window (in days) over which soft bounces are counted toward the threshold. Bounces older than the window do not count; the counter resets if the address has been quiet long enough. Shorter is more forgiving; longer catches slow-burn deliverability problems.',
            windowUpdatedBy: null,
            windowUpdatedAt: null,
        };
    },
    computed: {
        asSeconds() {
            const multiplier = this.unit === 'hours' ? 3600 : 60;
            return Math.round((this.throttleValue || 0) * multiplier);
        },
        isDirty() {
            return this.asSeconds !== this.savedSeconds;
        },
        savedHuman() {
            return this.humanizeSeconds(this.savedSeconds);
        },
        updatedMeta() {
            return this.formatUpdatedMeta(this.updatedAt, this.updatedBy);
        },
        isThresholdDirty() {
            return Number(this.thresholdValue) !== Number(this.savedThreshold);
        },
        thresholdUpdatedMeta() {
            return this.formatUpdatedMeta(this.thresholdUpdatedAt, this.thresholdUpdatedBy);
        },
        isWindowDirty() {
            return Number(this.windowValue) !== Number(this.savedWindowDays);
        },
        windowUpdatedMeta() {
            return this.formatUpdatedMeta(this.windowUpdatedAt, this.windowUpdatedBy);
        },
    },
    async mounted() {
        await this.fetchSettings();
    },
    methods: {
        humanizeSeconds(seconds) {
            if (!seconds) return '—';
            if (seconds % 3600 === 0) {
                const h = seconds / 3600;
                return `${h} hour${h === 1 ? '' : 's'}`;
            }
            const m = Math.round(seconds / 60);
            return `${m} minute${m === 1 ? '' : 's'}`;
        },
        formatUpdatedMeta(updatedAt, updatedBy) {
            if (!updatedAt) return '';
            let when = updatedAt;
            try {
                const d = new Date(updatedAt);
                if (!isNaN(d.getTime())) {
                    when = d.toLocaleString('en-US', {
                        year: 'numeric', month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                    });
                }
            } catch (_) { /* keep raw */ }
            // `updated_by` currently holds the raw cognito username (a UUID),
            // which is not useful to display. Hide it until the GET handler
            // is extended to JOIN against the user table and return a name.
            return `Last changed ${when}`;
        },
        async fetchSettings() {
            this.loading = true;
            try {
                const response = await service.getPlatformSettings();
                const settings = response?.data?.settings || [];
                const throttle = settings.find((s) => s.setting_key === THROTTLE_KEY);
                if (throttle) {
                    this.savedSeconds = Number(throttle.setting_value) || 3600;
                    this.throttleDescription = throttle.description || '';
                    this.updatedBy = throttle.updated_by || null;
                    this.updatedAt = throttle.updated_at || null;
                    this.applySecondsToInputs(this.savedSeconds);
                }
                const threshold = settings.find((s) => s.setting_key === SOFT_BOUNCE_THRESHOLD_KEY);
                if (threshold) {
                    this.savedThreshold = Number(threshold.setting_value) || 3;
                    this.thresholdValue = this.savedThreshold;
                    this.thresholdDescription = threshold.description || '';
                    this.thresholdUpdatedBy = threshold.updated_by || null;
                    this.thresholdUpdatedAt = threshold.updated_at || null;
                }
                const win = settings.find((s) => s.setting_key === SOFT_BOUNCE_WINDOW_KEY);
                if (win) {
                    this.savedWindowDays = Number(win.setting_value) || 30;
                    this.windowValue = this.savedWindowDays;
                    this.windowDescription = win.description || '';
                    this.windowUpdatedBy = win.updated_by || null;
                    this.windowUpdatedAt = win.updated_at || null;
                }
            } catch (err) {
                console.error('Failed to load platform settings:', err);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Could not load settings',
                    detail: 'Try refreshing the page. If the problem persists, contact support.',
                    life: 5000,
                });
            } finally {
                this.loading = false;
            }
        },
        // Pick the friendliest unit: whole hours if it divides evenly, else minutes.
        applySecondsToInputs(seconds) {
            if (seconds % 3600 === 0) {
                this.unit = 'hours';
                this.throttleValue = seconds / 3600;
            } else {
                this.unit = 'minutes';
                this.throttleValue = Math.round(seconds / 60);
            }
        },
        // Keep the numeric value sensible when the operator flips the unit:
        // convert the current value so the real duration is preserved.
        onUnitChange(event) {
            const newUnit = event.value;
            const currentSeconds = this.asSecondsForUnit(
                this.throttleValue,
                newUnit === 'hours' ? 'minutes' : 'hours'
            );
            if (newUnit === 'hours') {
                this.throttleValue = Math.max(1, Math.round(currentSeconds / 3600));
            } else {
                this.throttleValue = Math.max(1, Math.round(currentSeconds / 60));
            }
        },
        asSecondsForUnit(value, unit) {
            return (value || 0) * (unit === 'hours' ? 3600 : 60);
        },
        async save() {
            if (!this.isDirty) return;
            this.saving = true;
            try {
                await service.updatePlatformSetting(THROTTLE_KEY, this.asSeconds);
                this.savedSeconds = this.asSeconds;
                this.updatedAt = new Date().toISOString();
                this.$toast.add({
                    severity: 'success',
                    summary: 'Saved',
                    detail: `Throttle window set to ${this.humanizeSeconds(this.asSeconds)}.`,
                    life: 4000,
                });
                // Refresh to pick up server-recorded updated_by/updated_at.
                await this.fetchSettings();
            } catch (err) {
                console.error('Failed to save throttle window:', err);
                const detail = err?.response?.data?.error
                    || err?.response?.data?.['error:']
                    || 'The value was rejected. It must be between 1 minute and 24 hours.';
                this.$toast.add({
                    severity: 'error',
                    summary: 'Could not save',
                    detail,
                    life: 6000,
                });
            } finally {
                this.saving = false;
            }
        },
        async saveThreshold() {
            if (!this.isThresholdDirty) return;
            this.savingThreshold = true;
            try {
                await service.updatePlatformSetting(SOFT_BOUNCE_THRESHOLD_KEY, this.thresholdValue);
                this.savedThreshold = Number(this.thresholdValue);
                this.thresholdUpdatedAt = new Date().toISOString();
                this.$toast.add({
                    severity: 'success',
                    summary: 'Saved',
                    detail: `Soft bounce threshold set to ${this.savedThreshold}.`,
                    life: 4000,
                });
                await this.fetchSettings();
            } catch (err) {
                console.error('Failed to save soft-bounce threshold:', err);
                const detail = err?.response?.data?.error
                    || err?.response?.data?.['error:']
                    || 'The value was rejected. It must be an integer between 2 and 10.';
                this.$toast.add({
                    severity: 'error',
                    summary: 'Could not save',
                    detail,
                    life: 6000,
                });
            } finally {
                this.savingThreshold = false;
            }
        },
        async saveWindow() {
            if (!this.isWindowDirty) return;
            this.savingWindow = true;
            try {
                await service.updatePlatformSetting(SOFT_BOUNCE_WINDOW_KEY, this.windowValue);
                this.savedWindowDays = Number(this.windowValue);
                this.windowUpdatedAt = new Date().toISOString();
                this.$toast.add({
                    severity: 'success',
                    summary: 'Saved',
                    detail: `Soft bounce window set to ${this.savedWindowDays} day${this.savedWindowDays === 1 ? '' : 's'}.`,
                    life: 4000,
                });
                await this.fetchSettings();
            } catch (err) {
                console.error('Failed to save soft-bounce window:', err);
                const detail = err?.response?.data?.error
                    || err?.response?.data?.['error:']
                    || 'The value was rejected. It must be an integer between 7 and 90 days.';
                this.$toast.add({
                    severity: 'error',
                    summary: 'Could not save',
                    detail,
                    life: 6000,
                });
            } finally {
                this.savingWindow = false;
            }
        },
    },
};
</script>

<style scoped>
.platform-settings .card {
    padding: 2rem;
}

/* Header */
.settings-header {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 2rem;
}
.settings-header-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 50%;
    background: var(--primary-color, #00796b);
    color: var(--primary-color-text, #ffffff);
    flex-shrink: 0;
}
.settings-header-icon .pi {
    font-size: 1.25rem;
}
.settings-title {
    margin: 0 0 0.25rem 0;
    font-size: 1.35rem;
}
.settings-subtitle {
    margin: 0;
    color: var(--text-color-secondary);
    max-width: 640px;
    line-height: 1.5;
}

/* Settings grid — responsive auto-fit. Wide screens get 3 columns side-by-
   side, tablets fall to 2, phones to 1. The 340px minimum is the smallest
   width that keeps the control row (input + dropdown + Save) from wrapping
   on the throttle card. */
.settings-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
    gap: 1.5rem;
    /* Default grid align-items is `stretch` -- each card in a row grows to
       the tallest card's height. Combined with `margin-top: auto` on
       .setting-meta below, that keeps the Save buttons aligned and pins
       the "Last changed" footer to the bottom of every card. */
}

/* Setting card */
.setting-card {
    border: 1px solid var(--surface-border, #dee2e6);
    border-radius: 10px;
    padding: 1.5rem;
    background: var(--surface-section, #ffffff);
    display: flex;
    flex-direction: column;
}
.setting-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
}
.setting-label {
    font-weight: 600;
    font-size: 1.1rem;
    color: var(--text-color);
}
.setting-current-badge {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--primary-color, #00796b);
    background: var(--highlight-bg, rgba(0, 121, 107, 0.1));
    padding: 0.25rem 0.7rem;
    border-radius: 999px;
    white-space: nowrap;
}

/* Info callout */
.setting-callout {
    display: flex;
    gap: 0.65rem;
    background: var(--surface-100, #f1f5f9);
    border-left: 3px solid var(--primary-color, #00796b);
    border-radius: 6px;
    padding: 0.85rem 1rem;
    margin-bottom: 1.5rem;
}
.setting-callout .pi {
    color: var(--primary-color, #00796b);
    margin-top: 0.15rem;
    flex-shrink: 0;
}
.setting-callout p {
    margin: 0;
    color: var(--text-color-secondary);
    line-height: 1.55;
    font-size: 0.9rem;
}

/* Control row */
.setting-control {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    /* Absorb any vertical slack ABOVE the control row so the input + Save
       button line up across cards regardless of how tall each card's
       callout text is. Meta still pins to the bottom naturally. */
    margin-top: auto;
}
.throttle-input {
    width: 6.5rem;
}
.unit-dropdown {
    min-width: 8rem;
}
.save-btn {
    margin-left: auto;
}

/* Meta footer */
.setting-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    /* Generous horizontal gap when on one row, becomes the row-gap when the
       browser is forced to wrap to a second line on a narrow card. */
    gap: 0.5rem 1rem;
    flex-wrap: wrap;
    /* Control row above already has `margin-top: auto`, so it absorbs all
       the vertical slack. Meta then sits with its natural 1.25rem gap
       below the control row -- which lands at the bottom of the card. */
    margin-top: 1.25rem;
    padding-top: 1rem;
    border-top: 1px dashed var(--surface-border, #dee2e6);
    font-size: 0.8rem;
    color: var(--text-color-secondary);
}
.meta-equiv .pi {
    font-size: 0.75rem;
    margin-right: 0.3rem;
}
.meta-updated {
    font-style: italic;
}

/* Loading */
.loading-state {
    color: var(--text-color-secondary);
    padding: 2rem 0;
}
.loading-state .pi {
    margin-right: 0.4rem;
}

@media (max-width: 560px) {
    .save-btn {
        margin-left: 0;
        width: 100%;
    }
    .throttle-input,
    .unit-dropdown {
        width: 100%;
    }
}
</style>